import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_ADD_GENERAL_EXPEND, ROUTE_EDIT_GENERAL_EXPEND, ROUTE_GENERAL_EXPEND_LIST } from '../../constants/routeConstants';
import { Button, Card } from 'react-bootstrap';
import { customStorage, formatDateTime, getPayloadData, isInRole, isNullOrEmptyString, isNullOrUndefinedObj, scrollToFormError } from '../../services/UtilityService';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { cancelGeneralExpend, getGeneralExpendDetails } from '../../services/GeneralExpendService';
import { getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import { RG_ADMIN_ONLY } from '../../constants/roleConstants';

const GeneralExpendDetails = () => {
    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { generalExpendId } = useParams();
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [generalExpendData, setGeneralExpendData] = useState(null);
    const navigate = useNavigate();

    const initialLoad = async () => {
        setLoading(true);
        try {
            const lastLockedData = await getLastLockedYearMonth();
            setLastLockedYearMonth(lastLockedData || null);

            const response = await getGeneralExpendDetails((parseInt(generalExpendId) || 0));
            setGeneralExpendData(response);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("GENERAL EXPEND >> EXPEND DETAILS");
        initialLoad();
    }, []);

    const handleCancelGeneralExpend = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            await cancelGeneralExpend(generalExpendId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        navigate(ROUTE_GENERAL_EXPEND_LIST);
    };

    const canValidProcess = () => {
        const dateStr = generalExpendData?.expendedDate || '';
        const expendYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const expendMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = generalExpendData?.expendedById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < expendYear ||
            (lastLockedYearMonth.toYear === expendYear && lastLockedYearMonth.toMonth < expendMonth);
        return isValidUser && isValidLockDate;
    }

    return (
        <div id='generalExpendDetailsPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Expend Details"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    {/* Expended Reason */}
                                    <div className="mb-1">
                                        <label className='custom-label'>Expended Reason</label>
                                        <div className="w-100 label-box">
                                            {generalExpendData?.expendedReason}
                                        </div>
                                    </div>

                                    {/* Expended Price */}
                                    <div className="mb-1">
                                        <label className='custom-label'>Expended Price</label>
                                        <div className="w-100 label-box">
                                            {generalExpendData?.expendedPrice}¥
                                        </div>
                                    </div>

                                    {/* ? Expended Date as DatePicker */}
                                    <div className="mb-1">
                                        <label className='custom-label'>Expended Date(yyyyMMdd)</label><br />
                                        <div className="w-100 label-box">
                                            {formatDateTime(generalExpendData?.expendedDate)}
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className='custom-label'>Expended By</label>
                                        <div className="w-100 label-box">
                                            {generalExpendData?.expendedBy}
                                        </div>
                                    </div>

                                    {/* Comment */}
                                    <div className="mb-1">
                                        <label className='custom-label'>Comment</label>
                                        <div className="w-100 label-box input-area input-area">
                                            {generalExpendData?.comment}
                                        </div>
                                    </div>

                                    <div className='floating-bottom-btn-group mt-2'>
                                        <div className='floating-bottom-btn-grid'>
                                            <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="secondary"
                                                onClick={() => navigate(ROUTE_GENERAL_EXPEND_LIST)}
                                            >
                                                Go to Expend List
                                            </Button>
                                            {isInRole(RG_ADMIN_ONLY, currentUser?.role) &&<Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="warning"
                                                onClick={() => navigate(`${ROUTE_ADD_GENERAL_EXPEND}`)}
                                            >
                                                Add New Expend
                                            </Button>}
                                            {canValidProcess() && <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="secondary"
                                                onClick={() => navigate(`${ROUTE_EDIT_GENERAL_EXPEND}/${generalExpendId}`)}
                                            >
                                                Edit
                                            </Button>}
                                            {canValidProcess() &&
                                                <Button
                                                    className="appBtn w-100"
                                                    type="button"
                                                    variant="danger"
                                                    onClick={() => setShowConfirmDialog(true)}
                                                >
                                                    Cancel Expend
                                                </Button>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='cancelGeneralExpendConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to cancel this expend?'
                    onConfirm={handleCancelGeneralExpend}
                    onCancel={() => { setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your Expend has been successfully cancelled.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default GeneralExpendDetails;
